// TODO: cleanup with research_assistant_gen_3 ff
//
// Project-scoped "remember the last active chat" persistence. Keeps the
// user in their current chat as they navigate around the project (cohort
// builder tabs, artifact detail pages, anywhere within
// ``/projects/<pid>/...``) without making every navigation site
// responsible for threading ``?chat=`` through the URL.
//
// Storage: ``sessionStorage`` keyed by project id. Browser-tab scoped is
// the right grain — the user's intent ("I'm working in this chat") is a
// session-level fact, and tab close is a natural reset point.
//
// TTL: 30 minutes since the last sticky write. Long enough to survive a
// lunch break, short enough that an abandoned tab doesn't auto-route you
// back into a stale chat days later.
//
// Crucial property: the sticky fallback resolves SYNCHRONOUSLY in render
// (not in an effect) so consumers of the resolved chatId — most
// importantly ``MaybeActiveChatProvider`` — never observe a
// transiently-undefined chatId during URL gaps (e.g. cohort-builder tab
// nav drops ``?chat=`` for one render). Async URL repair runs in an
// effect, but it's cosmetic-only: the provider, useEpiChatPage mount,
// and socket subscribe all stay alive across the gap.

import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useInternalChatVisibilityEnabled } from './useInternalChatVisibilityEnabled';
import { projectPageKeys } from '../lib/api/queryKeys';
import type { ChatSession } from '../lib/api/types';
import { useProjectChatsQuery, type ProjectChatsCache } from '../lib/api/useProjectChats';

const STORAGE_KEY_PREFIX = 'epi-chat-active:';
const TTL_MS = 30 * 60 * 1000;

// Mirrors the empty-state composer's initial-message stash key (written in
// ``ChatPanelEmptyState`` and drained in ``ActiveChatBody``). MUST match the
// key those two use. See {@link hasPendingInitialMessage}.
const INITIAL_MESSAGE_KEY_PREFIX = 'epi-chat-initial:';

/**
 * Has the empty-state composer stashed an initial message for ``chatId``?
 *
 * The composer mints a client-side chat id, stashes the user's first message
 * under this key, and THEN navigates to ``?chat=<id>`` — all before the chat
 * row appears in the ``me`` list. The stash is therefore the signal that a
 * ``?chat=<id>`` not yet in the list is a chat the current user is creating
 * (so it's theirs), as opposed to someone else's chat reached via a shared
 * link (OR-6206). Honoring it keeps the optimistic-create flow from being
 * torn down before its row lists.
 */
const hasPendingInitialMessage = (chatId: string): boolean => {
  try {
    return sessionStorage.getItem(`${INITIAL_MESSAGE_KEY_PREFIX}${chatId}`) != null;
  } catch {
    return false;
  }
};

/**
 * ``?chat=new`` — explicit "+ New chat" intent (OR-6123). The user asked for a
 * fresh chat, so the entry-path fallback (reopen last-active / "chat before
 * it") must be SUPPRESSED and the welcome shell shown, even though the project
 * has live chats. A plain project entry (no ``?chat=``) still reopens the
 * last-active chat — the sentinel is what distinguishes "I want a new chat"
 * from "I just opened the project", which the no-param state alone cannot. The
 * sentinel persists in the URL until the empty-state composer creates a real
 * chat on first send (which navigates to ``?chat=<real-id>``).
 */
export const NEW_CHAT_SENTINEL = 'new';

interface StickyChat {
  chatId: string;
  lastActiveAt: number;
}

const storageKey = (projectId: string): string => `${STORAGE_KEY_PREFIX}${projectId}`;

const readSticky = (projectId: string): StickyChat | null => {
  if (!projectId) return null;
  try {
    const raw = sessionStorage.getItem(storageKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StickyChat;
    if (typeof parsed?.chatId !== 'string' || typeof parsed?.lastActiveAt !== 'number') {
      return null;
    }
    if (Date.now() - parsed.lastActiveAt > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeSticky = (projectId: string, chatId: string): void => {
  try {
    sessionStorage.setItem(
      storageKey(projectId),
      JSON.stringify({ chatId, lastActiveAt: Date.now() } satisfies StickyChat)
    );
  } catch {
    /* private browsing / quota — sticky stops working, panel falls back to empty state */
  }
};

/**
 * Explicit clear — call from the "Back to chats" and "chat deleted"
 * handlers so the user's intent ("show me the list / I removed this
 * chat") wins over the auto-restore effect.
 */
export const clearStickyActiveChat = (projectId: string): void => {
  if (!projectId) return;
  try {
    sessionStorage.removeItem(storageKey(projectId));
  } catch {
    /* ignore */
  }
};

/** Live chats sorted most-recently-active first. */
const sortByLastActiveDesc = (chats: ChatSession[]): ChatSession[] =>
  [...chats].sort((a, b) => b.lastActiveAt.localeCompare(a.lastActiveAt));

/**
 * AC3 fallback resolution against the loaded ``me`` chats list.
 *
 * Given the optimistic hint (sticky id) and the server's authoritative
 * ``lastActiveChatId``, resolve which chat to open on project entry:
 *
 *  1. sticky id, if it's still a live chat (the user's in-tab intent wins
 *     over the server value, which lags a switch by one PATCH);
 *  2. else the server ``lastActiveChatId``, if it's still a live chat;
 *  3. else "the chat before it" — the most-recently-active REMAINING chat
 *     (head of the ``lastActiveAt``-desc live list). ``lastActiveAt`` is bumped
 *     whenever the owner touches a chat (opening or sending), so head-of-
 *     remaining is the chat the user would most recently have been in;
 *  4. else ``undefined`` → drop to the welcome shell (no live chats remain).
 *
 * Only reached once the query has loaded; while loading the caller uses the
 * sync optimistic hint so the provider never sees a defined→undefined flap.
 */
const resolveFromList = (
  chats: ChatSession[],
  stickyChatId: string | undefined,
  serverLastActiveChatId: string | null
): string | undefined => {
  const liveIds = new Set(chats.map((c) => c.id));
  if (stickyChatId && liveIds.has(stickyChatId)) return stickyChatId;
  if (serverLastActiveChatId && liveIds.has(serverLastActiveChatId)) return serverLastActiveChatId;
  return sortByLastActiveDesc(chats)[0]?.id;
};

/**
 * Resolve the active chat id for ``projectId`` by falling URL → sticky
 * synchronously, and repair the URL in an effect so the address bar
 * matches the resolved state.
 *
 * Why a synchronous resolve (not an effect-driven redirect): callers
 * pass the result into {@link MaybeActiveChatProvider}. If the
 * provider ever saw ``chatId`` go from defined → undefined → defined
 * across a URL gap, it would unmount the ``ActiveChatProvider``,
 * tear down the ``useEpiChatPage`` mount, and drop the socket
 * subscribe — even though the URL gap was momentary. Resolving
 * synchronously means the provider sees ``chatId`` stay defined
 * across the gap.
 *
 * URL repair runs in an effect (one ``navigate({ replace: true })``
 * per gap) so users see the chat sidecar back in the address bar
 * for shareable URLs and refresh-survives. The repair is cosmetic —
 * the chat state has already been kept alive by the synchronous
 * resolve above.
 *
 * OR-6123 (AC2 / AC3) layering, in precedence order:
 *  - ``?chat=new`` is the explicit "+ New chat" sentinel (see
 *    {@link NEW_CHAT_SENTINEL}): resolve to ``undefined`` (welcome shell) and
 *    suppress the entry-path fallback, so a project with live chats still lets
 *    the user start a fresh one instead of being yanked back into the last
 *    chat. Not existence-checked, not URL-repaired, no sticky write.
 *  - any other ``?chat=<id>`` is authoritative ONLY when the current user may
 *    open that chat (OR-6206): it's one of their own live chats, OR a chat
 *    they're mid-creating (carries an initial-message stash — see
 *    {@link hasPendingInitialMessage}, the signal that distinguishes a
 *    brand-new optimistic chat from someone else's chat reached via a shared
 *    link). A ``?chat=<id>`` the user can't open is treated as a foreign
 *    shared link: it is NOT mounted (so its socket is never subscribed) and
 *    the user is routed to their own last-active chat / welcome shell via the
 *    same fallback-walk as the entry path, then the URL is repaired to match.
 *  - On entry (or while the ``me`` query is still loading and the URL chat
 *    can't yet be classified), fall to the sync optimistic hint (sticky ??
 *    most-recent-from-cache) so the provider stays stable and the user lands
 *    instantly — never on an unverified URL id that could be foreign.
 *  - Once the query has loaded, reconcile via {@link resolveFromList}:
 *    sticky-if-live → server ``lastActiveChatId``-if-live → head-of-remaining
 *    → ``undefined`` (welcome shell). A genuine "chat gone, none remain"
 *    teardown is desired — distinct from the momentary URL-gap teardown.
 *
 * NOTE (sharing): the ownership gate checks the ``me`` partition only. Chat
 * sharing is disabled today (every chat is private), so a foreign id is always
 * someone else's private chat. TODO: when project-shared chats land, also
 * honor a ``?chat=<id>`` that is in the ``team`` partition. cleanup with
 * research_assistant_gen_3 ff.
 *
 * Selecting a chat reorders it to the head of the ``me`` list (the server bumps
 * ``last_active_at`` on subscribe); a synchronous effect mirrors that into the
 * cache so the switcher reflects selection order immediately, without waiting
 * for a refetch.
 */
export const useEffectiveChatId = (projectId: string): string | undefined => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlChatId = searchParams.get('chat') ?? undefined;
  const isNewChatIntent = urlChatId === NEW_CHAT_SENTINEL;

  // Shares the cache with the panel's own ``useProjectChatsQuery`` reads —
  // no extra fetch. Drives the server-authoritative reconcile + the
  // existence-check/walk on the entry path.
  const { data: chatsData, isSuccess } = useProjectChatsQuery({ projectId, ownerFilter: 'me' });

  // OR-6206: is the URL ``?chat=<id>`` one the current user may actually open?
  // A chat in their own live list is genuinely theirs; a chat they're
  // mid-creating carries an initial-message stash (written before the
  // optimistic navigate, so it's present before the row lists). Anything else
  // — once the list has loaded — is someone else's chat from a shared link.
  // Computed here (ahead of the ``all``-partition query below) so it can
  // gate that query's ``enabled`` — see ``needsAllPartition``.
  const liveOwnIds = chatsData ? new Set(chatsData.data.map((c) => c.id)) : undefined;
  const isOwnUrlChat =
    urlChatId != null &&
    (hasPendingInitialMessage(urlChatId) || Boolean(liveOwnIds?.has(urlChatId)));

  // OR-6455: a ``?chat=<id>`` that's neither the caller's own nor a pending
  // create can still be legitimately openable — read-only — via the
  // internal-chat-visibility ``all`` partition. Without this, the OR-6206
  // guard below classified EVERY such id as "someone else's chat via a
  // shared link" and silently reverted the URL back to the caller's own
  // chat before ``ProjectChatPanel``/``useActiveChat`` ever got a chance to
  // resolve ``viaInternalVisibility`` and render the read-only view — the
  // click-to-open bug this fixes.
  //
  // ``needsAllPartition`` (not just ``internalVisibilityEnabled``) gates the
  // fetch: firing on every internal+flagged user's every project-page open
  // — own chats included — turned the ``owner=all`` WARNING audit line into
  // page-load noise and issued an unnecessary Auth0 lookup on every visit.
  // Only fire once the ``me`` partition has settled AND confirmed the URL's
  // chat (if any) is NOT already known to be this user's own — the
  // overwhelming majority of opens, own chats, never trigger it at all.
  const internalVisibilityEnabled = useInternalChatVisibilityEnabled();
  const needsAllPartition =
    internalVisibilityEnabled && urlChatId != null && isSuccess && !isOwnUrlChat;
  const {
    data: allChatsData,
    isSuccess: allIsSuccess,
    isError: allIsError,
  } = useProjectChatsQuery({
    projectId,
    ownerFilter: 'all',
    enabled: needsAllPartition,
  });

  // The user's in-tab "last chat" intent. Read unconditionally: it seeds the
  // no-URL entry path AND the fallback when a URL carries a chat the user
  // can't open (OR-6206). It never holds a foreign id — ``writeSticky`` only
  // ever persists a resolved (own) chat. The ``new`` sentinel is not a chat,
  // so it never seeds sticky.
  const stickyChatId = readSticky(projectId)?.chatId;

  // OR-6455: a foreign id found in the ``all`` partition — openable
  // read-only. Kept as its own flag (not folded into ``isOwnUrlChat``,
  // which several comments/consumers treat as "genuinely theirs") so
  // ``ProjectChatPanel``'s own ``viaInternalVisibility`` check remains the
  // single source of truth for "render read-only" — this hook only needs
  // to know "don't revert the URL away from this id".
  const liveInternalVisibilityIds =
    internalVisibilityEnabled && allChatsData
      ? new Set(allChatsData.data.map((c) => c.id))
      : undefined;
  const isInternalVisibilityUrlChat =
    urlChatId != null && Boolean(liveInternalVisibilityIds?.has(urlChatId));

  // Only safe to run the OR-6206 revert-to-own-chat walk once EVERY partition
  // that could legitimize a foreign ``?chat=<id>`` has settled — otherwise a
  // ``?chat=<all-partition-id>`` reverts on the first render (before
  // ``allChatsData`` loads) and never gets a second chance, since the
  // URL-repair effect below already overwrote it by the time the ``all``
  // query resolves. Gated on ``needsAllPartition`` (not
  // ``internalVisibilityEnabled``) to match the query's own ``enabled`` —
  // otherwise a URL with no ``?chat=`` at all would wait forever on an
  // ``all`` query that was never asked to run. Tolerates ``allIsError`` too:
  // a failed cross-owner lookup should still fall back to the user's own
  // last-active chat (the normal ``resolveFromList`` path below) rather than
  // leaving resolution permanently stuck.
  const readyToResolve = isSuccess && (!needsAllPartition || allIsSuccess || allIsError);

  let effectiveChatId: string | undefined;
  if (isNewChatIntent) {
    // Explicit "+ New chat": welcome shell, no fallback, no existence-check.
    effectiveChatId = undefined;
  } else if (urlChatId && (isOwnUrlChat || isInternalVisibilityUrlChat)) {
    // URL is the user's own chat (or one they're creating), or an
    // internal-visibility-openable foreign chat — both authoritative.
    effectiveChatId = urlChatId;
  } else if (readyToResolve && chatsData) {
    // List loaded with either no ``?chat=`` (entry path, AC3 reopen walk) or a
    // ``?chat=<foreign-id>`` the user can't open at all (OR-6206). Both
    // resolve to the user's OWN last-active chat / welcome shell — never the
    // foreign id.
    effectiveChatId = resolveFromList(chatsData.data, stickyChatId, chatsData.lastActiveChatId);
  } else {
    // List still loading and the URL chat (if any) isn't yet classifiable.
    // Use the optimistic in-tab hint so an own-chat refresh lands instantly;
    // an unverified URL id is NOT trusted here — mounting it would subscribe
    // the socket to a chat that might be someone else's. It's reconciled the
    // moment the relevant queries settle via the branches above.
    const cacheHead = chatsData ? sortByLastActiveDesc(chatsData.data)[0]?.id : undefined;
    effectiveChatId = stickyChatId ?? cacheHead;
  }

  // Persist whatever chat is currently effective. Fires once per
  // chat-change (deps are [projectId, effectiveChatId]); semantically
  // it's "while a chat is open, keep its sticky entry fresh enough to
  // beat the TTL".
  useEffect(() => {
    if (!projectId || !effectiveChatId) return;
    writeSticky(projectId, effectiveChatId);
  }, [projectId, effectiveChatId]);

  // Live reorder on select: opening a chat moves it to the head of the ``me``
  // cache with a fresh ``lastActiveAt`` — mirroring the server's subscribe →
  // ``last_active_at`` bump — so the switcher reflects selection order at once.
  // No-op when the chat is already the head (plain project entry resolves to
  // the head, so this never fights the entry path). Gated on ``isSuccess`` so
  // it only runs against a settled list, not the optimistic loading window.
  useEffect(() => {
    if (!projectId || !effectiveChatId || !isSuccess) return;
    const cacheKey = projectPageKeys.chatsForProject(projectId, 'me');
    queryClient.setQueryData<ProjectChatsCache>(cacheKey, (cur) => {
      if (!cur) return cur;
      const idx = cur.data.findIndex((c) => c.id === effectiveChatId);
      if (idx <= 0) return cur; // absent (optimistic id not yet listed) or already head
      const picked: ChatSession = { ...cur.data[idx], lastActiveAt: new Date().toISOString() };
      const rest = cur.data.filter((c) => c.id !== effectiveChatId);
      return { ...cur, data: [picked, ...rest], lastActiveChatId: effectiveChatId };
    });
  }, [projectId, effectiveChatId, isSuccess, queryClient]);

  // URL repair: keep the address bar in sync with the resolved chat so the URL
  // is shareable, survives refresh, and never advertises a chat the user can't
  // open. ``replace: true`` so the back button doesn't oscillate between
  // with-chat and no-chat URLs. Three cases:
  //  - resolved from sticky/server (URL had no chat)        → add ?chat=
  //  - foreign ?chat=<id> resolved to the user's own chat   → replace ?chat=
  //  - foreign ?chat=<id> and the user has no chats         → drop ?chat=
  //
  // Gated on ``readyToResolve`` (OR-6455: not just ``isSuccess``): a resolved
  // id is only written to the URL — where it becomes authoritative and is no
  // longer existence-checked — AFTER every partition that could legitimize
  // the URL's id has loaded and {@link resolveFromList} has confirmed it's a
  // live own chat (or ``undefined``). Repairing during the optimistic
  // loading window would write a provisional id before the walk could run —
  // for an internal-visibility-enabled caller, that includes waiting on the
  // ``all`` partition too, or a foreign-but-openable id gets reverted before
  // it ever gets a chance to resolve as ``isInternalVisibilityUrlChat``. The
  // ``new`` sentinel is intentionally sticky in the URL until the composer
  // creates a real chat, so it's never repaired away. The sync resolve above
  // keeps the provider mounted across the gap — only the URL write waits.
  useEffect(() => {
    if (isNewChatIntent || !readyToResolve) return;
    if (urlChatId === effectiveChatId) return;
    const next = new URLSearchParams(searchParams);
    if (effectiveChatId) {
      next.set('chat', effectiveChatId);
    } else {
      next.delete('chat');
    }
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  }, [
    isNewChatIntent,
    urlChatId,
    effectiveChatId,
    readyToResolve,
    searchParams,
    navigate,
    location.pathname,
  ]);

  return effectiveChatId;
};
